import { statesOptions } from 'constants/constantVariables'
import { isRiskCard, isExactOfac, isExactListHit } from './SourceTabs'

/**
 * Screening rules shared by the Healthcare Search board and drawer: what each list found for one
 * person or business, what is still open, and the single status that sums it up.
 *
 * Decisions are the saved records: CONFIRMED = "Save record" on a match, DISMISSED = part of
 * "Mark … as clear" (the note is stored on each one).
 */

export const LISTS = [
    { key: 'OFAC', label: 'OFAC', name: 'OFAC sanctions' },
    { key: 'LEIE', label: 'LEIE', name: 'HHS OIG LEIE' },
    { key: 'SAMGOV', label: 'SAM', name: 'SAM.gov' },
    { key: 'STATE_MEDICAID', label: 'Medicaid', name: 'State Medicaid' },
    { key: 'NPPES', label: 'NPPES', name: 'NPPES directory' },
]
export const EXCLUSION_LISTS = ['OFAC', 'LEIE', 'SAMGOV', 'STATE_MEDICAID']

// A decision covers a card only while the card's risk status is the one decided on: a re-run that
// turns an identity into an exclusion is open again.
export const decisionKey = (matchedKey, status) => `${matchedKey}|${status || ''}`
export const decisionFor = (decisions, card) => decisions.find(d => (
    decisionKey(d.matchedKey, d.status) === decisionKey(card.matchedKey, card.status))) || null

export const snapshotOf = (record) => {
    try { return JSON.parse(record.cardSnapshotJson) } catch (_) { return null }
}

export const hitsOn = (card, key) => (card?.hits || []).filter(h => h.source === key)
// Exact on this list: OFAC by name or alias, the others by NPI or full name.
export const isExactOn = (card, key, name, npi) => hitsOn(card, key).some(h => (key === 'OFAC'
    ? isExactOfac(h, name)
    : isExactListHit(h, name, npi)))

const stateName = (code) => statesOptions.USA.find(o => o.value === code)?.label || code
// "MI Medicaid" -> "Michigan Medicaid"; other lists by their short name.
export const listReason = (card, key) => {
    if (key === 'OFAC') return 'OFAC sanctions'
    if (key === 'STATE_MEDICAID') {
        const auth = hitsOn(card, key)[0]?.excludingAuthority || ''
        const m = /^([A-Z]{2}) Medicaid$/.exec(auth)
        return m ? `${stateName(m[1])} Medicaid` : 'State Medicaid'
    }
    return LISTS.find(l => l.key === key)?.label || key
}
export { stateName }

const latestOf = (records) => records.slice()
    .sort((a, b) => (b.decidedAt || b.createdAt || '').localeCompare(a.decidedAt || a.createdAt || ''))[0] || null

/**
 * Everything the board and drawer need for one person or business.
 * `entry` is the latest search (history row), `result` its stored response, `decisions` the saved
 * records for this person.
 */
export const screen = (entry, result, decisions) => {
    const name = entry?.request?.name
    const npi = entry?.request?.npi
    const sources = entry?.sources || result?.sources || []
    const cards = result?.cards || []
    const saved = decisions.filter(d => d.decision !== 'DISMISSED')
    const cleared = decisions.filter(d => d.decision === 'DISMISSED')

    const lists = {}
    for (const l of LISTS) {
        const src = sources.find(s => s.source === l.key)
        const onList = cards.filter(c => hitsOn(c, l.key).length)
        const decisionOf = (c) => decisionFor(decisions, c)
        // Once a record is saved on a list, its other similar names stop counting: the real one was found.
        // NPPES is a directory: an identity record saved there never marks the list as "saved".
        const savedHere = l.key === 'NPPES' ? [] : onList.filter(c => decisionOf(c)?.decision === 'CONFIRMED')
        const open = l.key === 'NPPES' || savedHere.length
            ? []
            : onList.filter(c => isRiskCard(c) && !decisionOf(c))
        lists[l.key] = {
            status: src?.status || null,          // OK | ERROR | DISABLED | null (not part of the search)
            // The drawer lists cards, so count cards once they're loaded; until then, the search's hit count.
            count: src?.status !== 'OK' ? 0 : result ? onList.length : (src.hitCount || 0),
            cards: onList,
            exact: l.key === 'NPPES' ? [] : onList.filter(c => isExactOn(c, l.key, name, npi)),
            open,
            openExact: open.filter(c => isExactOn(c, l.key, name, npi)),
            saved: savedHere,
            checked: l.key !== 'NPPES' && !open.length && !savedHere.length && onList.some(c => decisionOf(c)?.decision === 'DISMISSED'),
            source: src || null,
        }
    }

    const open = EXCLUSION_LISTS.flatMap(k => lists[k].open)
    const openCards = [...new Map(open.map(c => [c.matchedKey + '|' + c.status, c])).values()]
    const openExact = EXCLUSION_LISTS.reduce((n, k) => n + lists[k].openExact.length, 0)
    const ofacSaved = saved.filter(d => hitsOn(snapshotOf(d), 'OFAC').length)
    const otherSaved = saved.filter(d => !hitsOn(snapshotOf(d), 'OFAC').length
        && EXCLUSION_LISTS.some(k => hitsOn(snapshotOf(d), k).length))
    // A saved exclusion that has since been lifted (reinstated) isn't an active exclusion.
    const priorSaved = otherSaved.filter(d => snapshotOf(d)?.status === 'PRIOR_SANCTIONS')
    const activeSaved = otherSaved.filter(d => !priorSaved.includes(d))
    // "Mark … as clear" never touches a match that has a saved record under another status (e.g. a
    // re-run turned it from reinstated into excluded): replacing that record takes an explicit Save.
    const savedKeys = new Set(saved.map(d => d.matchedKey))
    const clearable = openCards.filter(c => !savedKeys.has(c.matchedKey))
    const errors = LISTS.filter(l => lists[l.key].status === 'ERROR')

    let status
    if (!entry) status = 'NOT_SEARCHED'
    else if (ofacSaved.length) status = 'SANCTIONED'
    else if (activeSaved.length) status = 'EXCLUDED'
    else if (priorSaved.length) status = 'PRIOR'
    else if (!result) status = 'LOADING'
    else if (openExact) status = 'EXACT'
    else if (openCards.length) status = 'TO_REVIEW'
    else if (errors.length) status = 'ERROR'
    else status = 'CLEAR'

    const savedRecord = latestOf(ofacSaved.length ? ofacSaved : activeSaved.length ? activeSaved : priorSaved)
    const savedCard = savedRecord ? snapshotOf(savedRecord) : null
    const savedOn = savedCard ? (ofacSaved.length ? 'OFAC' : EXCLUSION_LISTS.find(k => hitsOn(savedCard, k).length)) : null
    const clearedOpen = cleared.filter(d => cards.some(c => decisionFor([d], c)))

    return {
        status, lists, openCards, openExact, errors, clearable,
        savedRecord, savedReason: savedCard && savedOn ? listReason(savedCard, savedOn) : null,
        // Records on a sanctions or exclusion list; an NPPES identity record doesn't count.
        savedCount: ofacSaved.length + otherSaved.length,
        recordCount: saved.length,
        clearRecord: status === 'CLEAR' ? latestOf(clearedOpen) : null,
        clearRecords: clearedOpen,
        ofacExact: lists.OFAC.exact.length > 0,
    }
}

// Kept in its own file so the record card can use it without importing the screening rules.
export { programMeaning } from './ofacPrograms'
